import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit as fbLimit, orderBy as fbOrderBy, query as fbQuery } from 'firebase/firestore';
import { db } from '../config/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageInit } from '../hooks/usePageInit';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuthGuard } from '../hooks/useAuthGuard';
import BlockRenderer from '../components/BlockRenderer';
import './LandingPage.css';
import './FAQListPage.css';

type ContentBlock =
  | { id: string; type: 'richtext'; html: string }
  | { id: string; type: 'paragraph'; text: string }
  | { id: string; type: 'list'; items: string[] }
  | { id: string; type: 'image'; url: string; alt?: string };

type FAQDoc = {
  id: string;
  question?: string;
  answer?: string;
  blocks?: ContentBlock[];
  targets?: string[];
  target?: 'school' | 'student' | 'both' | 'lp';
  status?: 'visible' | 'hidden' | 'scheduled';
  publishedAt?: any;
  order?: number;
  categoryIds?: string[];
};

type FAQCategoryDoc = {
  id: string;
  name?: string;
  order?: number;
  status?: 'visible' | 'hidden';
};

function toMillis(v: any): number {
  try {
    if (!v) return 0;
    if (typeof v?.toMillis === 'function') return v.toMillis();
    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    return new Date(v).getTime();
  } catch {
    return 0;
  }
}

const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

export default function FAQListPage() {
  usePageInit();
  usePageTitle('よくある質問');
  useAuthGuard();

  const [faqs, setFaqs] = useState<FAQDoc[]>([]);
  const [categories, setCategories] = useState<FAQCategoryDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const nowMs = Date.now();
        setLoading(true);
        const faqSnap = await getDocs(
          fbQuery(collection(db, 'faqs'), fbOrderBy('order', 'asc'), fbLimit(200))
        );
        const faqItems: FAQDoc[] = faqSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        const filteredFaqs = faqItems.filter((f: any) => {
          const targets: string[] = Array.isArray(f?.targets) ? (f.targets as any[]).map((x) => String(x)) : [];
          const legacyTarget = String(f?.target || '');
          const isInList = targets.includes('faqList');
          const isLp = targets.includes('lp') || legacyTarget === 'lp';
          if (!isInList && !isLp) return false;
          const status = String(f?.status || 'visible');
          if (status === 'hidden') return false;
          if (status === 'scheduled') {
            const pubMs = toMillis(f?.publishedAt);
            if (!pubMs || pubMs > nowMs) return false;
          }
          return true;
        });

        if (!alive) return;
        setFaqs(filteredFaqs);
        if ((import.meta as any)?.env?.DEV) {
          // eslint-disable-next-line no-console
          console.log('[FAQListPage]', { total: faqItems.length, filtered: filteredFaqs.length });
        }

        // カテゴリは取得に失敗しても FAQ 本体は表示できるようにする
        try {
          const catSnap = await getDocs(
            fbQuery(collection(db, 'faqCategories'), fbOrderBy('order', 'asc'), fbLimit(200))
          );
          const catItems: FAQCategoryDoc[] = catSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          if (!alive) return;
          setCategories(catItems);
        } catch (e) {
          console.warn('Failed to load FAQ categories from Firestore:', e);
          if (!alive) return;
          setCategories([]);
        }
      } catch (e) {
        console.warn('Failed to load FAQ list content from Firestore:', e);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const visibleCategories = useMemo(() => {
    return (categories || [])
      .filter((c) => (c as any).status !== 'hidden')
      .sort((a: any, b: any) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [categories]);

  const categoryById = useMemo(() => {
    const m = new Map<string, FAQCategoryDoc>();
    visibleCategories.forEach((c) => m.set(c.id, c));
    return m;
  }, [visibleCategories]);

  const grouped = useMemo(() => {
    const byCatId = new Map<string, FAQDoc[]>();
    const uncategorized: FAQDoc[] = [];

    for (const f of faqs || []) {
      const ids = uniq(Array.isArray((f as any).categoryIds) ? (f as any).categoryIds.map((x: any) => String(x)) : []);
      if (!ids.length) {
        uncategorized.push(f);
        continue;
      }
      let added = false;
      for (const id of ids) {
        if (categoryById.has(id)) {
          const arr = byCatId.get(id) || [];
          arr.push(f);
          byCatId.set(id, arr);
          added = true;
        }
      }
      if (!added) uncategorized.push(f);
    }

    const sections = visibleCategories
      .map((c) => ({
        id: c.id,
        name: c.name || '（無題）',
        faqs: byCatId.get(c.id) || [],
      }))
      .filter((s) => s.faqs.length > 0);

    return { sections, uncategorized };
  }, [faqs, visibleCategories, categoryById]);

  const handleSmoothAnchorClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    const targetId = String(id || '').replace(/^#/, '');
    const el = document.getElementById(targetId);
    if (!el) return;
    e.preventDefault();
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // ハッシュも更新（履歴は汚さない）
      window.history.replaceState(null, '', `#${targetId}`);
    } catch {
      // fallback
      window.location.hash = `#${targetId}`;
    }
  };

  // FAQが動的になったので、レンダリング後にcollapsibleを再初期化
  useEffect(() => {
    const $ = (window as any).$;
    if ($?.fn?.collapsible) {
      setTimeout(() => {
        try {
          $('.collapsible').collapsible();
        } catch {}
      }, 0);
    }
  }, [grouped.sections.length, grouped.uncategorized.length, faqs.length]);

  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var faq-list-page-wrapper" id="main-wrap">
        <div className="main-wrap">
          <div id="home"></div>
          <Header />
          <main className="container-wrap">
            <section id="faq" className="space-top-short">
              <div className="root">
                <div className="container fixed-width">
                  <div className="row spacing6">
                    <div className="col-12 pa-6">
                      <div className="faq-list-back">
                        <a className="waves-effect btn-icon backtohome" href="/">
                          <span>
                            <i className="ion-ios-home-outline"></i>
                            <i className="ion-ios-arrow-thin-left"></i>
                          </span>
                        </a>
                      </div>
                      <div className="title-main align-left">
                        <h3>
                          <strong>FAQ一覧</strong>
                        </h3>
                      </div>
                      <p className="text faq-list-lead">
                        カテゴリ別にFAQをまとめています。該当しない場合は「その他」もご確認ください。
                      </p>
                      {!loading && ((grouped.sections || []).length > 0 || (grouped.uncategorized || []).length > 0) && (
                        <div className="faq-toc" aria-label="カテゴリ一覧">
                          {(grouped.sections || []).map((sec) => (
                            <a
                              key={sec.id}
                              className="faq-toc-link"
                              href={`#cat-${sec.id}`}
                              onClick={(e) => handleSmoothAnchorClick(e, `cat-${sec.id}`)}
                            >
                              {sec.name}
                            </a>
                          ))}
                          {(grouped.uncategorized || []).length > 0 && (
                            <a
                              className="faq-toc-link"
                              href="#cat-uncategorized"
                              onClick={(e) => handleSmoothAnchorClick(e, 'cat-uncategorized')}
                            >
                              その他
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {(grouped.sections || []).map((sec) => (
                    <div key={sec.id} className="row spacing6">
                      <div className="col-12 pa-6 faq-section-anchor" id={`cat-${sec.id}`}>
                        <h5 className="use-text-title2" style={{ marginBottom: 12 }}>
                          {sec.name}
                        </h5>
                        <div className="accordion">
                          <ul className="collapsible">
                            {sec.faqs.map((item, index) => (
                              <li key={`${sec.id}-${item.id}-${index}`} className={`accordion-content paper ${index === 0 ? 'active' : ''}`}>
                                <div className="collapsible-header content">
                                  <p className="heading">{item.question || ''}</p>
                                  <i className="material-icons right arrow">expand_more</i>
                                </div>
                                <div className="collapsible-body detail">
                                  {Array.isArray((item as any).blocks) && (item as any).blocks.length > 0 ? (
                                    <BlockRenderer blocks={(item as any).blocks as ContentBlock[]} />
                                  ) : (
                                    <p>{item.answer || ''}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(grouped.uncategorized || []).length > 0 && (
                    <div className="row spacing6">
                      <div className="col-12 pa-6 faq-section-anchor" id="cat-uncategorized">
                        <h5 className="use-text-title2" style={{ marginBottom: 12 }}>
                          その他
                        </h5>
                        <div className="accordion">
                          <ul className="collapsible">
                            {grouped.uncategorized.map((item, index) => (
                              <li key={`uncat-${item.id}-${index}`} className={`accordion-content paper ${index === 0 ? 'active' : ''}`}>
                                <div className="collapsible-header content">
                                  <p className="heading">{item.question || ''}</p>
                                  <i className="material-icons right arrow">expand_more</i>
                                </div>
                                <div className="collapsible-body detail">
                                  {Array.isArray((item as any).blocks) && (item as any).blocks.length > 0 ? (
                                    <BlockRenderer blocks={(item as any).blocks as ContentBlock[]} />
                                  ) : (
                                    <p>{item.answer || ''}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {!loading && (faqs || []).length === 0 && (
                    <div className="row spacing6">
                      <div className="col-12 pa-6">
                        <div className="card paper faq-empty-card">
                          <p className="faq-empty-title">現在、FAQ一覧に表示できるFAQがありません。</p>
                          <p className="faq-empty-body">
                            カテゴリ未設定でも「その他」として表示されます。管理画面でFAQの「表示対象（FAQ一覧 / LP）」とステータスをご確認ください。
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {loading && (
                    <div className="row spacing6">
                      <div className="col-12 pa-6">
                        <p style={{ margin: 0 }}>読み込み中...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </main>
          <Footer withDeco={true} />

          {/* PAGE NAV（ページトップへ戻る） */}
          <div className="hidden-md-down">
            <div className="page-nav" id="page_nav">
              <nav className="section-nav">
                <div className="scrollnav">
                  <ul>
                    {(() => {
                      const links = [
                        ...(grouped.sections || []).map((sec) => ({
                          id: `cat-${sec.id}`,
                          tooltip: sec.name,
                        })),
                        ...(((grouped.uncategorized || []).length > 0)
                          ? [{ id: 'cat-uncategorized', tooltip: 'その他' }]
                          : []),
                      ];
                      // 下から積み上げる（LPトップに合わせて 30px 刻み）
                      const step = 30;
                      return links.map((l, idx) => {
                        const topPx = step * (links.length - idx);
                        return (
                          <li key={l.id} style={{ top: `${topPx}px` }}>
                            <a
                              className="tooltipped"
                              href={`#${l.id}`}
                              data-position="left"
                              data-tooltip={l.tooltip}
                              onClick={(e) => handleSmoothAnchorClick(e, l.id)}
                            ></a>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                </div>
              </nav>
              <div className="scrollnav">
                <a
                  className="btn-floating btn-large primary tooltipped waves-effect waves-light"
                  href="#home"
                  data-position="left"
                  data-tooltip="To Top"
                  onClick={(e) => handleSmoothAnchorClick(e, 'home')}
                >
                  <i className="icon material-icons">arrow_upward</i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

